// act-as-cdn-domain-detail-page.tsx — CDN domain detail page (implicit customer-token context).
// Route: /operations/act-as/cdn/:domainId
// Header: Breadcrumbs → h1 + status pill (no PageHeader — ops convention).
// 23-tab shell: all tabs implemented (A1–A4).
// Guard: EmptyState when no impersonation session is active for any org.
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { DetailHeader } from '@/components/detail-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { getImpersonationSession } from '@/lib/impersonation-session';
import { setImpersonationOrgId } from '@/api/impersonation-client';
import { useActAsCDNDomain } from '@/api/act-as-cdn';
import { CdnDomainOverviewTab } from './tabs/cdn-domain-overview-tab';
import { CdnDomainCacheTab } from './tabs/cdn-domain-cache-tab';
import { CdnDomainOriginTab } from './tabs/cdn-domain-origin-tab';
import { CdnDomainOriginRewriteTab } from './tabs/cdn-domain-origin-rewrite-tab';
import { CdnDomainHeaderRulesTab } from './tabs/cdn-domain-header-rules-tab';
import { CdnDomainErrorPagesTab } from './tabs/cdn-domain-error-pages-tab';
import { CdnDomainRedirectsTab } from './tabs/cdn-domain-redirects-tab';
import { CdnDomainPurgeWarmTab } from './tabs/cdn-domain-purge-warm-tab';
import { CdnDomainSslTlsTab } from './tabs/cdn-domain-ssl-tls-tab';
// A4 — Security
import { CdnDomainWafTab } from './tabs/cdn-domain-waf-tab';
import { CdnDomainFirewallRulesTab } from './tabs/cdn-domain-firewall-rules-tab';
import { CdnDomainRateLimitingTab } from './tabs/cdn-domain-rate-limiting-tab';
import { CdnDomainAccessControlTab } from './tabs/cdn-domain-access-control-tab';
import { CdnDomainCorsTab } from './tabs/cdn-domain-cors-tab';
import { CdnDomainSecurityHeadersTab } from './tabs/cdn-domain-security-headers-tab';
import { CdnDomainVerifiedBotsTab } from './tabs/cdn-domain-verified-bots-tab';
import { CdnDomainBotManagementTab } from './tabs/cdn-domain-bot-management-tab';
import { CdnDomainWafAnalyticsTab } from './tabs/cdn-domain-waf-analytics-tab';
import { CdnDomainBotAnalyticsTab } from './tabs/cdn-domain-bot-analytics-tab';
// A4 — Optimization + Operations
import { CdnDomainOptimizationTab } from './tabs/cdn-domain-optimization-tab';
import { CdnDomainOperationsTab } from './tabs/cdn-domain-operations-tab';
import {
  CDN_TABS,
  CDN_TAB_LABEL,
  CDN_TAB_SECTIONS,
  DEFAULT_TAB,
  type CdnTab,
} from './cdn-domain-tab-registry';

type PillTone = 'success' | 'warning' | 'danger' | 'muted' | 'neutral';
function domainStatusTone(s: string | undefined): PillTone {
  if (s === 'active') return 'success';
  if (s === 'deploying' || s === 'pending') return 'warning';
  if (s === 'suspended' || s === 'error') return 'danger';
  if (s === 'terminated') return 'muted';
  return 'neutral';
}

export default function ActAsCdnDomainDetailPage() {
  const { domainId = '' } = useParams<{ domainId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  // Guard: bail early when no impersonation session is active for any org.
  const session = getImpersonationSession();
  // Re-activate the org context so impersonation-client sends the customer token
  // on all subsequent API calls (the org detail page cleanup may have cleared it).
  if (session) setImpersonationOrgId(session.orgId);
  if (!session) {
    return (
      <Container size="full">
        <EmptyState
          icon={<Globe size={32} />}
          title={t('cdn_domain.no_session_title', 'Act-as session required')}
          description={t(
            'cdn_domain.no_session_description',
            'Bật act-as để xem — activate an act-as session from the service detail page.',
          )}
        />
      </Container>
    );
  }

  // URL-persisted tab — unknown/missing slugs fall back to overview.
  const rawTab = searchParams.get('tab') ?? '';
  const activeTab: CdnTab = (CDN_TABS as readonly string[]).includes(rawTab)
    ? (rawTab as CdnTab)
    : DEFAULT_TAB;

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const domainQ = useActAsCDNDomain(domainId);

  if (domainQ.isLoading) {
    return <Container size="full"><TabLoading rows={4} /></Container>;
  }
  if (domainQ.isError || !domainQ.data) {
    return (
      <Container size="full">
        <TabError
          title={t('cdn_domain.detail_load_error', 'Failed to load CDN domain')}
          onRetry={() => domainQ.refetch()}
        />
      </Container>
    );
  }

  const domain = domainQ.data;

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <DetailHeader
          parent={[
            { label: 'Operations', to: '/operations' },
            { label: t('menu.operations_services', 'Services'), to: '/operations/services' },
            { label: 'CDN', to: `/operations/orgs/${session.orgId}?tab=cdn` },
          ]}
          current={domain.domain}
          title={domain.domain}
          meta={
            domain.status ? (
              <StatusPill tone={domainStatusTone(domain.status)}>{domain.status}</StatusPill>
            ) : null
          }
        />

        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList style={{ flexWrap: 'wrap' }}>
            {CDN_TAB_SECTIONS.map(({ label, tabs }) => (
              <span key={label || tabs[0]} style={{ display: 'contents' }}>
                {label && (
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: 'var(--vn-font-size-xs)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--vn-fg-subtle)',
                      alignSelf: 'center',
                      padding: '0 var(--vn-space-1)',
                      userSelect: 'none',
                    }}
                  >
                    {label}
                  </span>
                )}
                {tabs.map((tab) => (
                  <TabsTrigger key={tab} value={tab}>
                    {CDN_TAB_LABEL[tab]}
                  </TabsTrigger>
                ))}
              </span>
            ))}
          </TabsList>

          <TabsContent value="overview">
            {activeTab === 'overview' && <CdnDomainOverviewTab domainId={domainId} />}
          </TabsContent>

          {/* ── A2: Caching tabs ──────────────────────────────────────── */}
          <TabsContent value="cache">
            {activeTab === 'cache' && <CdnDomainCacheTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="upstream">
            {activeTab === 'upstream' && <CdnDomainOriginTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="origin-rewrite">
            {activeTab === 'origin-rewrite' && <CdnDomainOriginRewriteTab domainId={domainId} />}
          </TabsContent>

          {/* ── A3: Rules tabs ────────────────────────────────────────── */}
          <TabsContent value="header-rules">
            {activeTab === 'header-rules' && <CdnDomainHeaderRulesTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="error-pages">
            {activeTab === 'error-pages' && <CdnDomainErrorPagesTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="redirects">
            {activeTab === 'redirects' && <CdnDomainRedirectsTab domainId={domainId} />}
          </TabsContent>

          {/* ── A3: Delivery tabs ─────────────────────────────────────── */}
          <TabsContent value="purge">
            {activeTab === 'purge' && <CdnDomainPurgeWarmTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="ssl">
            {activeTab === 'ssl' && <CdnDomainSslTlsTab domainId={domainId} />}
          </TabsContent>

          {/* ── A4: Optimization + Operations ────────────────────────── */}
          <TabsContent value="optimization">
            {activeTab === 'optimization' && <CdnDomainOptimizationTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="operations">
            {activeTab === 'operations' && <CdnDomainOperationsTab domainId={domainId} />}
          </TabsContent>

          {/* ── A4: Security tabs ─────────────────────────────────────── */}
          <TabsContent value="security/waf">
            {activeTab === 'security/waf' && <CdnDomainWafTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/firewall-rules">
            {activeTab === 'security/firewall-rules' && <CdnDomainFirewallRulesTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/waf-analytics">
            {activeTab === 'security/waf-analytics' && <CdnDomainWafAnalyticsTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/rate-limiting">
            {activeTab === 'security/rate-limiting' && <CdnDomainRateLimitingTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/access-control">
            {activeTab === 'security/access-control' && <CdnDomainAccessControlTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/cors">
            {activeTab === 'security/cors' && <CdnDomainCorsTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/security-headers">
            {activeTab === 'security/security-headers' && <CdnDomainSecurityHeadersTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/verified-bots">
            {activeTab === 'security/verified-bots' && <CdnDomainVerifiedBotsTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/bot-management">
            {activeTab === 'security/bot-management' && <CdnDomainBotManagementTab domainId={domainId} />}
          </TabsContent>
          <TabsContent value="security/bot-management-analytics">
            {activeTab === 'security/bot-management-analytics' && <CdnDomainBotAnalyticsTab domainId={domainId} />}
          </TabsContent>
        </Tabs>
      </div>
    </Container>
  );
}

